import React, { useState, useEffect, useRef, useCallback } from 'react';
import Form from 'global/components/form';
import { get, set, orderBy, first, size, isObject, upperFirst } from '../../../global/lib/lodash';
import { ADMIN_FORM_CONTAINER, ADMIN_SUB_SCREEN, ADMIN_TITLE, AdminContentRoot, CreateSc } from './styles';
import { Column, Row } from '../../../global/components/row';
import { Text } from 'global/components/text';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Box, Checkbox, Icon, IconButton } from '@mui/material';
import { Button } from '../../../global/components/Button';
import { SortableList } from 'global/components/sortable-list';
import CheckpointForm from '../../../global/components/CheckpointForm';
import { getDisplayScoreLabel } from 'global/utils/getDisplayScore';
import ScoreIcon from 'global/components/ScoreIcon';
import { _size, convertToPostStructure } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SnackAlerts from '../../../global/components/snack-alerts';
import Modal from 'global/components/modal';
import { isNewItem, makeArray, makeArrayFrom, scoringClass, wait } from '../../../global/utils';
import { useLocalId } from '../../../global/hooks/useLocalId';
import { useScreen } from '../../../global/styles';
import { EditNoteIcon } from 'global/components/PerformIcons';
import { useTheme } from '@mui/material/styles';

export const YesNo = [
  { value: 1, title: 'Yes' },
  { value: -1, title: `No` },
];

//Because MUI is not good at displaying empty values in ui
export const fixMinus = (obj) => {
  const newobj = {};
  for (let k in obj) {
    const val = obj[k];
    if (val === -1) {
      newobj[k] = 0;
    } else if (val === 'null') {
      newobj[k] = null;
    } else if (val === '0') {
      newobj[k] = 0;
    } else {
      newobj[k] = val;
    }
  }
  return newobj;
};

export const fixBool = (v) => {
  if (v === undefined) {
    return undefined;
  }
  if (v === true || v === false) {
    return v;
  }
  return Boolean(parseInt(v));
};

const formatScoreLabels = (v, isCopy = false) => {
  if (!v) {
    return [];
  }
  if (isCopy) {
    return v;
  }

  return (v || []).filter((o) => o.value !== undefined).map((o) => ({ label: o.value, value: parseFloat(o.property) }));
};

export const CreateScorecard = (props) => {
  const [alerts, setAlerts] = useState();
  const [fs, setfs] = useState({});
  const [state, setState] = useState({
    loading: false,
  });

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      params: {
        include: 'checklists,checklists.classification,checklists.classification.system',
      },
    }
  );

  const { data: supersets } = useRetrieveData({ url: '/supersets' }, { build: ['supersets'], params: { include: 'system', filter: { access_override: 1 } } });

  const formatData = async () => {
    const obj = {
      type: 'scorecards',
    };

    const hasSelectedScorecard = fs.scorecard ? get(scorecards, 'data', []).find((s) => s.id == fs.scorecard) : undefined;

    const config = hasSelectedScorecard ? get(hasSelectedScorecard, 'config') : fs.config;

    const sysWeights = get(fs, 'weights', []);

    const orderOverrides = [];
    const weightOverrides = [];

    const fullSystems = await Promise.all(
      sysWeights.map(async (w) => {
        const { superset } = w;
        if (isNewItem({ id: w.key })) {
          const result = await sendData(
            {
              url: '/supersets',
              method: 'post',
              data: {
                data: {
                  type: 'supersets',
                  attributes: {
                    name: superset.name,
                  },
                },
              },
            },
            { normalize: { build: ['supersets'] } }
          );
          return {
            ...w,
            superset: {
              ...superset,
              ...first(result),
            },
          };
        } else {
          return w;
        }
      })
    );

    const checks = [];

    fullSystems.map((w, i) => {
      const { superset, items } = w;
      orderOverrides.push({ type: 'Superset', value: i, id: superset.id });

      if (!!superset.weight || superset.weight === 0) {
        weightOverrides.push({ type: 'Superset', value: superset.weight, id: superset.id });
      }

      items.map((cl, j) => {
        orderOverrides.push({ type: 'Checklist', value: j, id: cl.id });

        if (!!cl.weight || cl.weight === 0) {
          weightOverrides.push({ type: 'Checklist', value: cl.weight, id: cl.id });
        }

        checks.push({ type: 'checklists', id: cl.id, pivot: { superset_id: superset.id } });
      });
    });

    set(config, 'orderOverrides', orderOverrides);
    set(config, 'weightOverrides', weightOverrides);

    set(
      obj,
      'attributes',
      fixMinus({
        name: get(fs, 'name', ''),
        edition_id: get(props, 'editionId'),
        realm_id: get(props, 'doc.realm.id'),
        config: convertToPostStructure(fixMinus(config)),
        active: fs.active,
        'created-at': new Date().toISOString(),
        'updated-at': new Date().toISOString(),
      })
    );

    set(obj, 'relationships.checklists', { data: checks });

    if (get(props, 'edition.realms')) {
      set(obj, `relationships.realms`, { data: get(props, 'edition.realms', []).map((r) => ({ type: 'realms', id: r.id })) });
    }

    [
      { sel: 'attributes.config.score_value_label', conv: formatScoreLabels },
      { sel: 'attributes.config.sub_score_value_label', conv: formatScoreLabels },
    ].map((o) => set(obj, o.sel, o.conv(get(obj, o.sel), !!hasSelectedScorecard)));

    [
      { sel: 'attributes.config.has_sub_scores', conv: fixBool },
      { sel: 'attributes.config.edit_score_info', conv: fixBool },
      { sel: 'attributes.config.text_override', conv: fixBool },
      { sel: 'attributes.config.sub_scoring_class', conv: (v) => get(fixMinus({ score: v }), 'score') },
      {
        sel: 'attributes.config.allowed_tiers',
        conv: (v) =>
          !!hasSelectedScorecard
            ? v
            : !v
              ? null
              : Object.keys(v)
                  .map((k) => !!get(v, k) && k)
                  .filter((f) => !!f),
      },
    ].map((o) => set(obj, o.sel, o.conv(get(obj, o.sel))));

    return {
      data: obj,
    };
  };

  const onSubmit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));

      const data = await formatData();

      const resp = await sendData(
        {
          url: `/scorecards`,
          method: 'post',
          data,
        },
        { normalize: { build: ['scorecards'] } }
      );

      props.onClose && props.onClose(true, first(resp));
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const isCustom = get(fs, 'copy', 1) < 0;

  const edocs = get(props, 'doc.editionDocuments', []).filter((e) => e?.documentableType === 'checklist').map((e) => ({ ...e.document, order: parseInt(get(e, 'classification.system.id')) }));
  const scorecard = !isCustom ? scorecards.data.find((sc) => sc.id == get(fs, 'scorecard')) : undefined;
  const docs = orderBy(edocs, 'order');

  return (
    <CreateSc>
      <Form
        onFormState={(f) => setfs((ff) => ({ ...ff, ...f }))}
        showPosition
        onSubmit={onSubmit}
        loading={state.loading}
        screens={[
          {
            title: 'Step 1: General Information',
            positionTitle: 'Info',
            inputs: [
              {
                id: 'name',
                type: 'TextField',
                defaultValue: () => get(props, 'doc.name', ''),
                required: true,
                props: {
                  label: 'Name',
                },
              },
              {
                id: 'active',
                type: 'Select',
                defaultValue: () => 1,
                data: [
                  { value: 1, title: 'True' },
                  { value: -1, title: 'False' },
                ],
                props: {
                  label: 'Active',
                },
              },
              {
                id: 'copy',
                type: 'Select',
                defaultValue: () => 1,
                data: [
                  { value: 1, title: 'Yes' },
                  { value: -1, title: `No, I'll set it up myself` },
                ],
                props: {
                  label: 'Would you like to copy info from a previous scorecard?',
                },
              },
              isCustom
                ? {
                    id: 'config',
                    component: () => <CustomComponent {...fs} onFormState={(f) => setfs((fs) => ({ ...fs, config: f }))} />,
                  }
                : {
                    id: 'scorecard',
                    type: 'Select',
                    data: scorecards.data,
                    required: get(fs, 'copy') === 1,
                    item: (item, i) => {
                      return { value: item.id, title: item.name };
                    },
                    props: {
                      label: 'Scorecard',
                    },
                  },
            ],
          },
          {
            title: 'Step 2: Choose Checklists',
            positionTitle: 'Checklists',
            inputs: [
              {
                id: 'checklists',
                component: () => <EdDocList {...fs} {...props} scorecard={scorecard} docs={docs} onFormState={(f) => setfs((fs) => ({ ...fs, checklists: f }))} />,
              },
            ],
          },
          {
            title: 'Step 3: Order and Weights',
            positionTitle: 'Details',
            inputs: [
              {
                id: 'weights',
                component: () => <Weights {...fs} {...props} supersets={supersets} scorecard={scorecard} docs={docs} onFormState={(f) => setfs((fs) => ({ ...fs, weights: f }))} />,
              },
            ],
          },
        ]}
      />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </CreateSc>
  );
};

const CustomComponent = (props) => {
  const [fs, setfs] = useState(props.config || {});
  const [scores, setScores] = useState({});
  const formref = useRef();

  const maxScore = parseInt(fs.maxScore || 1);
  const minScore = parseInt(fs.minScore || 0);
  const step = parseInt(fs.step || 1);
  const isCount = fs.displayCountType !== 'average';

  const { isMobile } = useScreen();

  const _fs = fixMinus(fs);

  const updateStateFromType = useCallback(
    (f, fOld) => {
      const DEF = {
        1: {
          //cat and location
          textOverride: -1,
          defaultDisplay: 'category',
          defaultScore: 'null',
          hasSubScores: 1,
          editScoreInfo: 1,
        },
        2: {
          //cat only
          fallbackMinScore: 'null',
          textOverride: 1,
          defaultScore: 'null',
          defaultDisplay: 'category',
          hasSubScores: -1,
          editScoreInfo: 1,
        },
        3: {
          //location only
          fallbackMinScore: 'null',
          defaultScore: 100,
          defaultDisplay: 'realm',
          textOverride: -1,
          hasSubScores: 1,
          editScoreInfo: -1,
        },
      };

      const d = DEF[f.type];
      if (d) {
        Object.keys(d).map((k) => {
          if (formref.current) {
            formref.current.updateState(k, d[k], true, false);
          }
        });
        formref.current.updateState('type', f.type);
      }
    },
    [fs.type]
  );

  const updateStateFromCalc = useCallback(
    (f, fOld) => {
      const DEF = {
        1: {
          //Aggregated
          scoringClass: 'aggregated',
          displayCountType: 'average',
        },
        2: {
          //Simple Average
          scoringClass: 'simple',
          displayCountType: 'average',
        },
        3: {
          //Count Up
          scoringClass: 'simple',
          displayCountType: 'count_up',
        },
        4: {
          //Count Down
          scoringClass: 'simple',
          displayCountType: 'count_down',
        },
      };

      const d = DEF[f.calc];
      if (d) {
        Object.keys(d).map((k) => {
          if (formref.current) {
            formref.current.updateState(k, d[k], true, false);
          }
        });
        formref.current.updateState('calc', f.calc);
      }
    },
    [fs.calc]
  );

  const onFormState = (f) => {
    setfs((ff) => {
      if (f.type != ff.type) {
        updateStateFromType(f, ff);
      }
      if (f.calc != ff.calc) {
        updateStateFromCalc(f, ff);
      }
      return {
        ...ff,
        ...f,
      };
    });
  };

  useEffect(() => {
    props.onFormState({ ...(fs || {}), subScoringClass: 'min' });
  }, [fs]);

  const defaultScoreOptions = (opts = _fs || {}) => {
    const ret = [];

    const { allowNA, allowNO } = opts;
    if (!!allowNA) {
      ret.push({ title: 'N/A', value: 'null' });
    }

    if (!!allowNO) {
      ret.push({ title: 'N/O', value: -1 });
    }

    getScOptions().map((s) => {
      ret.push({ title: s.value, value: `${s.value}` });
    });

    return ret;
  };

  const onScore = (d, key) => {
    const data = d.build();

    setScores((s) => ({ ...s, [key]: parseFloat(get(data, 'data.attributes.score')) }));
  };

  const getScOptions = () => {
    const ret = [];

    const safeStep = step || 1;
    for (let i = minScore; (i <= maxScore && i >= minScore) || (i >= maxScore && i <= minScore); i += safeStep) {
      const value = Math.round(((i - minScore) * 100) / (maxScore - minScore || 1));
      // const usePercent = !!get(_fs, 'displayPercentage');
      ret.push({
        scorecardId: 1,
        checklistId: 1,
        value,
        text: i,
        criteria: '',
        weight: 1,
      });
    }

    return ret;
  };

  const score = (() => {
    const { sum, count } = Object.keys(scores).reduce(
      (agg, item) => {
        if (!isNaN(scores[item])) {
          if (scores[item] === -1) {
            if (get(agg, 'sum') === null) {
              agg.sum = -1;
            }
          } else {
            if (get(agg, 'sum') === -1 || isNaN(get(agg, 'sum'))) {
              agg.sum = 0;
            }
            agg.sum += scores[item];
            agg.count += 1;
          }
        }
        return agg;
      },
      { sum: null, count: 0 }
    );

    if (sum >= 0) {
      return { value: sum / count, weight: count };
    } else {
      return { value: sum };
    }
  })();

  const scorecardOptions = getScOptions();

  const formProps = {
    checkpointdata: {
      data: {
        name: 'Title',
        description: 'Checkpoint description and some additional text to make it look right',
        scorecardOptions,
      },
    },
    assessmentId: '1',
    checklist: { checklistId: 1 },
    pssvCheckpoint: { scorecardId: 1, checklistId: 1, checkpointId: 1 },
    scorecard: { config: { ..._fs, scoreValueLabel: formatScoreLabels(_fs.scoreValueLabel), maxScore, minScore, step } },
    assessment: {},
    hideRightGrid: true,
  };

  const CONT = isMobile ? Column : Row;

  const scoreLabels = scorecardOptions.map((o) => ({ label: _fs.displayPercentage ? `${o.value}%` : o.text, id: get(o, 'value', '').toString() }));

  return (
    <AdminContentRoot isMobile={isMobile}>
      <div>
        <Text bold>General Config</Text>
        <Form
          onFormState={onFormState}
          showAllScreens
          ref={formref}
          formStyle={ADMIN_FORM_CONTAINER}
          screens={[
            {
              inputs: [
                {
                  id: 'type',
                  type: 'ToggleButton',
                  defaultValue: () => 1,
                  options: [
                    { title: 'Category and Location', value: 1 },
                    { title: 'Category Only', value: 2 },
                    { title: 'Location Only', value: 3 },
                  ],
                  props: {
                    label: 'Scoring Type',
                    exclusive: true,
                  },
                },
                {
                  id: 'calc',
                  type: 'ToggleButton',
                  defaultValue: () => 1,
                  options: [
                    { title: 'Aggregated', value: 1 },
                    { title: 'Simple Average', value: 2 },
                    { title: 'Count Up', value: 3 },
                    { title: 'Count Down', value: 4 },
                  ],
                  props: {
                    label: 'Scoring Calculator',
                    helperText:
                      '"Aggregated" calculates averages at each tier whereas "Simple" is an average of all scored items. "Count Up" shows scores as a ratio of the total scored. "Count Down" shows the scores as an accumulation of negative points for failed items.',
                    exclusive: true,
                  },
                },
                {
                  id: 'allowedTiers',
                  type: 'Checkbox',
                  defaultValue: () => get(fs, 'allowedTiers'),
                  options: [
                    { label: 'Division', value: 'division' },
                    { label: 'Community', value: 'community' },
                    { label: 'Lot', value: 'lot' },
                  ],
                  props: {
                    label: 'Allowed Location Types',
                    helperText: 'Please select which types of locations this scorecard can be used for. If none are selected, all values are applicable.',
                  },
                },
                {
                  id: 'displayPercentage',
                  type: 'Select',
                  defaultValue: () => get(fs, 'displayPercentage', -1),
                  data: YesNo,
                  props: {
                    label: 'Display Scores as Percentages',
                  },
                },
                fs.type != 3 && {
                  id: 'textOverride',
                  type: 'Select',
                  defaultValue: () => get(fs, 'textOverride', -1),
                  data: YesNo,
                  props: {
                    label: 'Allow Text Overrides',
                  },
                },
                // {
                //   id: 'hasSubScores',
                //   type: 'Select',
                //   defaultValue: () => get(fs, 'hasSubScores', 1),
                //   data: YesNo,
                //   props: {
                //     label: 'Enable Sub-scores',
                //   },
                // },
                // {
                //   id: 'editScoreInfo',
                //   type: 'Select',
                //   defaultValue: () => get(fs, 'editScoreInfo', 1),
                //   data: YesNo,
                //   props: {
                //     label: 'Allow Scores to be Edited',
                //     helperText: 'Sub-scores can be used to automatically calculate score values, making it unnecessary in some circumstances to require users to edit the scores directly. Set to “No” to disable direct editing of scores.'
                //   },
                // },
                // {
                //   id: 'subScoringClass',
                //   type: 'Select',
                //   defaultValue: () => get(fs, 'subScoringClass', 'null'),
                //   data: [
                //     { title: 'None', value: 'null' },
                //     { title: 'Min', value: 'min' },
                //     { title: 'Max', value: 'max' },
                //     { title: 'Avg', value: 'avg' },
                //     { title: 'Count', value: 'count' },
                //   ].filter(f => !!f),
                //   props: {
                //     label: 'Sub-score Calculator',
                //   },
                // },
                {
                  id: 'defaultDisplay',
                  type: 'Select',
                  defaultValue: () => get(fs, 'defaultDisplay', 'category'),
                  data: [
                    { title: 'By Category', value: 'category' },
                    { title: 'By Location', value: 'realm' },
                  ].filter((f) => !!f),
                  props: {
                    label: 'Default Display',
                    disabled: fs.type == 2,
                  },
                },
              ].filter((f) => !!f),
            },
            [
              {
                title: 'Option Config',
                titleStyle: ADMIN_TITLE,
                screenContainer: {
                  props: {
                    className: ADMIN_SUB_SCREEN,
                  },
                },
                inputs: [
                  {
                    id: 'allowNA',
                    type: 'Select',
                    defaultValue: () => get(fs, 'allowNA', 1),
                    data: YesNo,
                    props: {
                      label: 'Allow N/A',
                    },
                  },
                  {
                    id: 'allowNO',
                    type: 'Select',
                    defaultValue: () => get(fs, 'allowNO', 1),
                    data: YesNo,
                    props: {
                      label: 'Allow N/O',
                    },
                  },
                  {
                    id: 'minScore',
                    type: 'TextField',
                    defaultValue: () => get(fs, 'minScore', 0),
                    props: {
                      label: 'Min Score',
                      type: 'number',
                    },
                  },
                  {
                    id: 'step',
                    type: 'TextField',
                    defaultValue: () => get(fs, 'step', 1),
                    props: {
                      label: 'Step',
                      type: 'number',
                      inputProps: {
                        min: 1,
                      },
                    },
                  },
                  {
                    id: 'maxScore',
                    type: 'TextField',
                    defaultValue: () => get(fs, 'maxScore', 1),
                    props: {
                      label: 'Max Score',
                      type: 'number',
                    },
                  },
                  {
                    id: 'defaultScore',
                    type: 'Select',
                    defaultValue: () => get(fs, 'defaultScore', 'null'),
                    data: defaultScoreOptions({ allowNO: true, allowNA: fs.type != 3 }),
                    props: {
                      label: 'Default Score',
                      displayEmpty: true,
                    },
                  },
                  fs.type != 2 && {
                    id: 'fallbackMinScore',
                    type: 'Select',
                    defaultValue: () => get(fs, 'fallbackMinScore', 'null'),
                    data: defaultScoreOptions({ allowNO: false, allowNA: true }),
                    props: {
                      label: 'Fallback Min Score',
                      displayEmpty: true,
                      disabled: fs.type == 3,
                    },
                  },
                  {
                    id: 'scoreValueLabel',
                    type: 'JsonBuilder',
                    defaultValue: () => get(fs, 'scoreValueLabel'),
                    defaultProps: {
                      blankAllowed: true,
                    },
                    addBtn: {
                      title: 'ADD SCORE LABEL',
                    },
                    options: [...(fs.allowNO > 0 ? [{ id: '-1', label: 'N/O' }] : []), ...scoreLabels].map((o) => ({
                      ...o,
                      label: o.label || `${o.id}`,
                      props: {
                        blankAllowed: true,
                        placeholder: 'Label',
                      },
                    })),
                    props: {
                      label: 'Score Labels',
                    },
                  },
                  fs.type != 2 && {
                    id: 'subScoreValueLabel',
                    type: 'JsonBuilder',
                    defaultValue: () => get(fs, 'subScoreValueLabel'),
                    addBtn: {
                      title: 'ADD INSTANCE LABEL',
                      disabled: fs.hasSubScores < 0,
                    },
                    options: [-1, 0, 100]
                      .map((o) => ({ id: o.toString() }))
                      .map((o) => ({
                        ...o,
                        label: o.id === '-1' ? 'Note' : `${o.id}`,
                        props: {
                          placeholder: 'Label',
                        },
                      })),
                    props: {
                      label: 'Instance Labels',
                    },
                  },
                ],
              },
              {
                title: 'Preview',
                titleStyle: ADMIN_TITLE,
                screenContainer: {
                  props: {
                    className: ADMIN_SUB_SCREEN,
                  },
                },
                inputs: [
                  {
                    id: 'display',
                    component: () => (
                      <Box sx={{ marginLeft: isMobile ? 0 : 10 }}>
                        <Row>
                          <span>{getDisplayScoreLabel(isCount ? score : { value: score.value }, null, formProps.scorecard.config)}</span>
                          <ScoreIcon score={score.value} />
                          <Text>Checklist</Text>
                        </Row>
                        <Box sx={{ margin: isMobile ? 8 : undefined }}>
                          <CheckpointForm hideInfoIcon={true} {...formProps} addToQueue={(d) => onScore(d, '1')} />
                          <CheckpointForm hideInfoIcon={true} {...formProps} addToQueue={(d) => onScore(d, '2')} />
                        </Box>
                      </Box>
                    ),
                  },
                ],
              },
            ],
          ]}
        />
      </div>
    </AdminContentRoot>
  );
};

const EdDocList = (props) => {
  const docs = props.docs;

  const [sel, setsel] = useState({});

  const widths = [
    { width: '7%', style: { minWidth: 42 }, title: '' },
    { width: '73%', title: 'Title', sel: 'name' },
    { width: '20%', title: 'Category', sel: 'classification.system.name' },
  ];

  useEffect(() => {
    props.onFormState(sel);
  }, [sel]);

  useEffect(() => {
    if (!_size(sel)) {
      const ss = {};
      const editionDocChecks = get(props, 'doc.editionDocuments', [])
        .filter((f) => get(f, 'documentableType') == 'checklist')
        .map((d) => `${d.documentableId}`);
      const scchecks = get(props, 'scorecard.checklists', []);
      const isCustom = get(props, 'copy', 1) < 0;

      docs.map((d) => {
        ss[d.id] = isCustom ? true : !!scchecks.find((s) => s.id == d.id || d.parentId == s.id) && !!editionDocChecks.includes(d.id);
      });

      setsel(ss);
    }
  }, []);

  return (
    <Box>
      <Row fullWidth sx={{ borderBottom: '1px solid black' }}>
        {widths.map((w) => (
          <Box key={w.title} sx={{ width: w.width, ...get(w, 'style', {}) }}>
            <Text>{w.title}</Text>
          </Box>
        ))}
      </Row>

      {docs.map((edoc) => {
        const issel = !!get(sel, edoc.id);
        return (
          <Row fullWidth>
            {widths.map((w) => {
              return (
                <Box sx={{ width: w.width, ...get(w, 'style', {}) }}>
                  {!w.title ? <Checkbox checked={issel} onChange={() => setsel((s) => ({ ...s, [edoc.id]: !issel }))} /> : <Text>{get(edoc, w.sel)}</Text>}
                </Box>
              );
            })}
          </Row>
        );
      })}
    </Box>
  );
};

const Weights = (props) => {
  const { supersets } = props;
  const nextLocalId = useLocalId();
  const [cls, setcls] = useState([]);
  const [state, setState] = useState({ force: 0, scorecards: [props.scorecard] });
  const [editModal, setEditModal] = useState({
    visible: false,
    onClose: () => {
      setEditModal((s) => ({ ...s, visible: false }));
    },
  });

  const widths = [
    { width: '9%', title: '' },
    { width: '91%', title: 'Title', sel: 'name' },
  ];

  const autoOrder = () => {
    alert('TODO');
  };

  const onSort = (order) => {
    setcls(order);
  };

  const renderSubItem = (item, fullItem) => {
    const hasWeight =
      !!item.weight || item.weight === 0
        ? item.weight
        : get(
            get(props, 'scorecard.config.weightOverrides', []).find((f) => f.id == item.idMatch && f.type == 'Checklist'),
            'value'
          );

    item.weight = hasWeight;

    return (
      // Top spacing is applied as margin (not padding) so it sits OUTSIDE the
      // row's content box — the SortableList drag handle centers on that box, so
      // padding here would push the handle above the title. Supersets stay on
      // paddingTop because their bottom divider balances the box. (PFM-2634)
      <Box sx={{ marginTop: '15px' }}>
        <Row>
          <IconButton onClick={() => editItem(item, fullItem)} size="small">
            <EditNoteIcon />
          </IconButton>
          {item.name}
        </Row>
        {(!!hasWeight || hasWeight === 0) && <Text type="label_small">{`Weight: ${hasWeight}`}</Text>}
      </Box>
    );
  };

  const renderItem = (item) => {
    const { superset, items } = item;

    const hasWeight =
      !!superset.weight || superset.weight === 0
        ? superset.weight
        : get(
            get(props, 'scorecard.config.weightOverrides', []).find((f) => f.id == superset.id && f.type == 'Superset'),
            'value'
          );

    superset.weight = hasWeight;

    return (
      <Box sx={{ position: 'relative', paddingTop: '15px' }}>
        <Row>
          <IconButton onClick={() => editSuperset(superset)} size="small">
            <EditNoteIcon />
          </IconButton>
          {superset.name}
          {!size(items) && (
            <IconButton sx={{ marginLeft: 'auto' }} onClick={() => deleteSuperset(item)} size="small">
              <Icon>delete</Icon>
            </IconButton>
          )}
        </Row>
        {(!!hasWeight || hasWeight === 0) && <Text type="label_small">{`Weight: ${hasWeight}`}</Text>}

        <Box sx={{ width: 'calc(100% + 20px)', marginLeft: '-20px', marginTop: '10px', borderBottom: '1px solid black' }} />
      </Box>
    );
  };

  const closeModal = () => {
    setEditModal((s) => ({
      ...s,
      visible: false,
      superset: undefined,
      item: undefined,
      isNew: false,
      onClose: () => {
        setEditModal((s) => ({ ...s, visible: false }));
      },
    }));
  };

  const addNew = () => {
    setEditModal((s) => ({
      ...s,
      title: 'Add New Category',
      visible: !s.visible,
      isNew: true,
      supersets,
      onClose: (fs) => {
        if (!!fs) {
          const isFullSystem = !!get(fs, 'name.name');
          const superset = isFullSystem ? fs.name : fs;
          const obj = {
            superset: {
              ...superset,
              weight: fs.weight,
            },
            items: [],
            key: superset.id || `new_${nextLocalId()}`,
          };
          setcls((c) => [...c, { ...obj, content: renderItem(obj) }]);
        }

        closeModal();
      },
    }));
  };

  const editSuperset = (superset) => {
    setEditModal((s) => ({
      ...s,
      title: 'Edit Category',
      visible: !s.visible,
      superset,
      supersets,
      onClose: (fs) => {
        if (!!fs) {
          Object.keys(fs).map((k) => {
            if (!isObject(fs[k])) {
              set(superset, k, fs[k]);
            }
          });
          setState((s) => ({ ...s, force: s.force + 1 }));
        }

        closeModal();
      },
    }));
  };

  const deleteSuperset = (superset) => {
    setcls((cl) => cl.filter((c) => c.key !== superset.key));
  };

  const editItem = (item, fullItem) => {
    setEditModal((s) => ({
      ...s,
      title: 'Edit Checklist',
      visible: !s.visible,
      item,
      supersets,
      onClose: (fs, editProps) => {
        if (!!fs) {
          Object.keys(fs).map((k) => {
            if (k !== 'moveTo') {
              set(item, k, fs[k]);
            }
          });

          if (fs.moveTo) {
            const newcls = [...get(editProps, 'list', [])].map((c) => {
              if (c.key == fullItem.key) {
                c.items = c.items.filter((f) => f.id !== item.id);
              }
              if (c.key == fs.moveTo) {
                c.items = [...c.items, item];
                c.items = c.items.map((it) => ({ ...it, content: () => renderSubItem(it, c) }));
              }
              return c;
            });
            setcls(newcls);
          } else {
            const newcls = [...get(editProps, 'list', [])].map((c) => {
              if (c.key == fullItem.key) {
                c.items = c.items.map((_item) => {
                  if (item.id === _item.id) {
                    return { ...item, content: () => renderSubItem(item, c) };
                  } else {
                    return _item;
                  }
                });
              }
              return c;
            });
            setcls(newcls);
          }

          setState((s) => ({ ...s, force: s.force + 1 }));
        }

        closeModal();
      },
    }));
  };

  useEffect(() => {
    props.onFormState && props.onFormState(cls);
  }, [cls, state]);

  useEffect(() => {
    const sel = get(props, 'checklists', {});

    const validchecks = props.docs.filter((d) => !!sel[d.id]);

    const systems = {};

    const scorechecks = get(props, 'scorecard.checklists');

    validchecks.map((check) => {
      const system = get(check, 'classification.system');
      const scorecheck = scorechecks ? scorechecks.find((c) => c.id == check.id || c.id == check.parentId) : {};

      check = {
        ...check,
        ...(scorecheck || {}),
        id: get(check, 'id'),
        idMatch: get(scorecheck, 'id', get(check, 'id')),
      };

      const superset = props.supersets.find((s) => (check.supersetId ? s.id == check.supersetId : get(s, 'system.id') == system.id));
      if (superset) {
        set(systems, superset.id, {
          superset,
          items: [...get(systems, [superset.id, 'items'], []), check],
        });
      }
    });

    const arr = orderBy(
      Object.keys(systems).map((k, i) => {
        const { superset, items } = systems[k];

        const order = get(props, 'scorecard.config.orderOverrides', []).find((f) => f.id == superset.id && f.type == 'Superset') || i;

        const obj = {
          superset,
          items: orderBy(
            items.map((item, _i) => {
              const itemorder = get(props, 'scorecard.config.orderOverrides', []).find((f) => f.id == item.idMatch && f.type == 'Checklist') || _i;
              return {
                ...item,
                order: get(itemorder, 'value', _i),
                weight: get(
                  get(props, 'scorecard.config.weightOverrides', []).find((f) => f.id == item.idMatch && f.type == 'Checklist'),
                  'value',
                  undefined
                ),
                key: item.id,
              };
            }),
            'order'
          ),
          order: get(order, 'value', i),
          key: superset.id,
        };

        obj.items = obj.items.map((item) => ({ ...item, content: () => renderSubItem(item, obj) }));

        return obj;
      }),
      'order'
    );

    setcls(arr);
  }, []);

  return (
    <Box>
      <Row>
        <Text bold>Checklists</Text>
        <Button icon={<Icon>refresh</Icon>} variant="text" color="secondary" title="Auto Order" onClick={autoOrder} />
      </Row>

      <Row fullWidth sx={{ borderBottom: '1px solid black' }}>
        {widths.map((w) => (
          <Box key={w.title} sx={{ width: w.width }}>
            <Text>{w.title}</Text>
          </Box>
        ))}
      </Row>

      <SortableList items={cls.map((c) => ({ ...c, content: renderItem(c) }))} verticalOnly onSort={onSort} />

      <Box sx={{ marginTop: '8px' }}>
        <Button title="Add New Category" onClick={addNew} icon={<Icon>add</Icon>} variant="outlined" />
      </Box>

      <EditModal {...editModal} list={cls} />
    </Box>
  );
};

const EditModal = (props) => {
  const theme = useTheme();
  const [formState, setFormState] = useState({});

  const onClose = (fs) => {
    props.onClose(fs, props);
  };

  const visible = !!props.visible;

  const item = props.item || props.superset;

  const list = props.list || [];

  const isSuper = !!props.isNew || !!props.superset;

  return (
    <Modal {...props} open={visible} onClose={() => onClose()}>
      {!!visible && (
        <Form
          showAllScreens
          onFormState={setFormState}
          screens={[
            {
              inputs: [
                {
                  id: 'name',
                  type: isSuper ? 'Autocomplete' : 'TextField',
                  defaultValue: () => get(item, 'name', ''),
                  data: () => props.supersets || [],
                  props: {
                    label: 'Name',
                    disabled: !props.isNew,
                    filterType: 'customsearch',
                    helperText: () => {
                      if (!props.isNew) {
                        return null;
                      }

                      return get(formState, 'name.id') || !get(formState, 'name') ? null : <Box component="span" sx={{ color: theme.palette.error.main }}>{`You are creating a new Category`}</Box>;
                    },
                  },
                },
                {
                  id: 'weight',
                  type: 'TextField',
                  defaultValue: () => get(item, 'weight', ''),
                  props: {
                    label: 'Weight',
                    type: 'number',
                  },
                },
                props.item && {
                  id: 'moveTo',
                  type: 'Select',
                  data: list.map((l) => ({ title: get(l, 'superset.name'), value: get(l, 'key') })),
                  props: {
                    label: 'Move to',
                  },
                },
              ].filter((f) => !!f),
            },
          ]}
        />
      )}
      <Button title="Save" onClick={() => onClose(formState)} />
    </Modal>
  );
};
